<template lang="pug">

div

    //- Bezeichnung und Unternehmen
    .equal.width.fields

        //- Bezeichnung
        text-field(label='Bezeichnung' v-model='name' v-bind:isRequired='true')
            field-error(v-bind:validation='validation' attribute='name')

        //- Unternehmen
        multi-dropdown-field(
            v-show='!hideCompany'
            label='Unternehmen'
            v-bind:options='companyModels'
            v-bind:is-disabled='disabledCompany'
            v-bind:is-required='true'
            v-bind:observe-changes='false'
            placeholder='Unternehmen auswählen'
            v-model='companyIds'
        )
            field-error(v-bind:validation='validation' attribute='companies')

    //- Versandhinweis je Unternehmen
    .field(v-for='link in companies' v-bind:key='link.company')
        label Versandhinweis für {{ companyName(link.company) }}
        text-field(
            v-bind:value='link.notice'
            v-on:input='updateCompanyNotice(link.company, $event)'
        )

    //- Firma
    text-field(label='Firma' v-bind:is-required='true' v-model='firm')
        field-error(v-bind:validation='validation' attribute='firm')

    //- Adresszusatz
    text-field(label='Adresszusatz' v-model='additional')

    //- Straße
    text-field(label='Straße' v-model='street' v-bind:isRequired='true')
        field-error(v-bind:validation='validation' attribute='street')

    //- PLZ und Ort
    .ui.two.fields

        //- PLZ
        text-field(label='PLZ' v-model='zip' v-bind:isRequired='true')
            field-error(v-bind:validation='validation' attribute='zip')

        //- Ort
        text-field(label='Ort' v-model='city' v-bind:isRequired='true')
            field-error(v-bind:validation='validation' attribute='city')

    //- Land
    dropdown-field(
        label='Land'
        v-bind:options='countryModels'
        v-bind:isRequired='true'
        v-model='country'
    )
        field-error(v-bind:validation='validation' attribute='country')

    //- Bundesland
    dropdown-field(
        label='Bundesland'
        v-bind:options='states'
        v-model='state'
        v-show='states.length > 0'
    )

    //- Ansprechpartner (Warenempfänger)
    .equal.width.fields
        text-field(label='Ansprechpartner (Name)' v-model='contactName')
        text-field(label='Telefon' v-model='contactPhone')
        text-field(label='E-Mail' v-model='contactEmail')

</template>

<script>
    import * as R from 'ramda'
    import { Validator } from 'simple-vue-validator'
    import DropdownField from '@/components/form/DropdownField.vue'
    import FieldError from '@/components/form/SmartFieldError'
    import MultiDropdownField from '@/components/form/MultiDropdownField.vue'
    import TextField from '@/components/form/TextField.vue'

    //noinspection JSUnusedGlobalSymbols,ReservedWordAsName
    export default {
        name: 'addresses-form-general-information',

        components: { DropdownField, FieldError, MultiDropdownField, TextField },

        computed: {

            additional: {
                get() { return this.value.additional || '' },
                set(value) { this.updateValue('additional', value) }
            },

            city: {
                get() { return this.value.city || '' },
                set(value) { this.updateValue('city', value) }
            },

            companies: {
                get() { return this.value.companies || [] },
                set(companyIds) {
                    const current = this.value.companies || []
                    const links = (companyIds || []).map(id => {
                        const existing = current.find(link => link.company === id)
                        return existing ? { ...existing } : { company: id, notice: '' }
                    })
                    this.updateValue('companies', links)
                }
            },

            companyIds: {
                get() { return (this.value.companies || []).map(link => link.company) },
                set(ids) { this.companies = ids }
            },

            contactEmail: {
                get() { return this.value.contactEmail || '' },
                set(value) { this.updateValue('contactEmail', value) }
            },

            contactName: {
                get() { return this.value.contactName || '' },
                set(value) { this.updateValue('contactName', value) }
            },

            contactPhone: {
                get() { return this.value.contactPhone || '' },
                set(value) { this.updateValue('contactPhone', value) }
            },

            country: {
                get() { return this.value.country },
                set(value) { this.updateValue('country', value) }
            },

            firm: {
                get() { return this.value.firm || '' },
                set(value) { this.updateValue('firm', value) }
            },

            name: {
                get() { return this.value.name || '' },
                set(value) { this.updateValue('name', value) }
            },

            state: {
                get() { return this.value.state || '' },
                set(value) { this.updateValue('state', value) }
            },

            states() {
                return this.$store.getters['states/allByCountryId'](this.value.country)
            },

            street: {
                get() { return this.value.street || '' },
                set(value) { this.updateValue('street', value) }
            },

            zip: {
                get() { return this.value.zip || '' },
                set(value) { this.updateValue('zip', value) }
            }

        },

        created() {
            // ggf Bundeslaender laden
            if (!R.isNil(this.value.country)) {
                this.$store.dispatch('states/loadAllByCountry', { countryId: this.value.country })
            }
        },

        methods: {

            updateValue(prop, value) {
                this.$emit('input', R.assoc(prop, value, this.value))
            },

            updateCompanyNotice(companyId, notice) {
                const companies = (this.value.companies || []).map(link =>
                    link.company === companyId ? { ...link, notice } : link
                )
                this.updateValue('companies', companies)
            },

            companyName(companyId) {
                const model = this.companyModels.find(c => c.id === companyId)
                return model ? model.name : ''
            },

            validate() {
                return this.$validate()
            }

        },

        props: {

            companyModels: {
                type: Array,
                default: () => []
            },

            countryModels: {
                type: Array,
                required: true
            },

            disabledCompany: {
                type: Boolean,
                default: false
            },

            disableName: {
                type: Boolean,
                default: false
            },

            hideCompany: {
                type: Boolean,
                default: false
            },

            value: {
                type: Object,
                required: true
            }
        },

        validators: {
            city(value) {
                return Validator.value(R.defaultTo('', value))
                    .required('Darf nicht leer sein. Bitte einen gültige Stadt angeben')
            },

            companies(value) {
                return Validator.custom(() => {
                    if (this.disabledCompany === false && this.hideCompany === false && (R.isNil(value) || value.length === 0)) {
                        return 'Darf nicht leer sein. Bitte mindestens ein Unternehmen angeben'
                    }
                })
            },

            country(value) {
                return Validator.value(R.defaultTo('', value))
                    .required('Darf nicht leer sein. Bitte ein Land auswählen')
            },

            firm(value) {
                return Validator.value(R.defaultTo('', value))
                    .required('Darf nicht leer sein')
            },

            name(value) {
                return Validator.value(R.defaultTo('', value))
                    .required('Darf nicht leer sein. Bitte einen gültigen Namen angeben')
            },

            street(value) {
                return Validator.value(R.defaultTo('', value))
                    .required('Darf nicht leer sein. Bitte einen gültige Straße angeben')
            },

            zip(value) {
                return Validator.value(R.defaultTo('', value))
                    .required('Darf nicht leer sein. Bitte einen gültige PLZ angeben')
            }
        },

        watch: {
            'country'(countryId) {
                // Bundeslaender laden
                this.$store.dispatch('states/loadAllByCountry', { countryId })

                // Selektiertes Bundesland entfernen
                this.$delete(this.value, 'state')
            }
        }

    }

</script>
